<template>
  <section class="p-4 space-y-6">
    <h3>Zusammenfassung</h3>
    <dl class="divide-y divide-dark-3 not-prose">
      <div class="flex justify-between items-start py-2">
        <dt>Ticketnummer</dt>
        <dd class="font-medium text-right" data-testid="tkd-ticket-number">
          {{ tkdServiceModel.ticketNumber }}
        </dd>
      </div>

      <div class="flex justify-between items-start py-2">
        <dt>Fehlerbeschreibung</dt>
        <dd class="font-medium text-right" data-testid="tkd-error-description">
          {{ tkdServiceModel.errorDescription }}
        </dd>
      </div>

      <div class="flex justify-between items-start py-2">
        <dt>Fremdverschulden</dt>
        <dd class="font-medium text-right" data-testid="tkd-third-party-fault">
          {{ tkdServiceModel.thirdPartyFault ? 'Ja' : 'Nein' }}
        </dd>
      </div>

      <div class="flex justify-between items-start py-2">
        <dt>Service Bericht</dt>
        <dd class="font-medium text-right" data-testid="tkd-service-report">
          {{ tkdServiceModel.report }}
        </dd>
      </div>
    </dl>

    <h3>Serviceleistungen</h3>
    <div class="mt-4 grid gap-10 md:grid-cols-2">
      <section>
        <h4 class="mt-0">Gerät</h4>
        <dl class="divide-y divide-dark-3 not-prose">
          <div class="flex justify-between items-start py-2">
            <dt>Serviceleistung</dt>
            <dd
              class="font-medium text-right"
              data-testid="tkd-service-report-type"
            >
              {{ report?.reportType?.name }}
            </dd>
          </div>

          <template v-if="report?.serviceDate">
            <div class="flex justify-between items-start py-2">
              <dt>Service Datum</dt>
              <dd class="font-medium text-right" data-testid="tkd-service-date">
                {{ report.serviceDate }}
              </dd>
            </div>
          </template>

          <template v-if="report?.counterPressure">
            <div class="flex justify-between items-start py-2">
              <dt>Ventilgegendruck</dt>
              <dd
                class="font-medium text-right"
                data-testid="tkd-counter-pressure"
              >
                {{ report.counterPressure }}
              </dd>
            </div>
          </template>

          <template v-if="report?.note">
            <div class="flex justify-between items-start py-2">
              <dt>Service Notizen</dt>
              <dd class="font-medium text-right" data-testid="tkd-service-note">
                {{ report.note }}
              </dd>
            </div>
          </template>

          <template v-if="report?.reason">
            <div class="flex justify-between items-start py-2">
              <dt>Grund</dt>
              <dd
                class="font-medium text-right"
                data-testid="tkd-service-reason"
              >
                {{ report.reason?.name }}
              </dd>
            </div>
          </template>

          <template v-if="report?.oldDevice">
            <div class="flex justify-between items-start py-2">
              <dt>
                Altes Gerät
                <div class="text-sm font-normal">Einbaudatum</div>
              </dt>
              <dd class="font-medium text-right" data-testid="tkd-old-device">
                {{ report.oldDevice?.serialNumber }}
                ({{ report.oldDevice?.customizedNumber }})
                <div class="text-sm font-normal">
                  {{ report.oldDevice?.mountDate }}
                </div>
              </dd>
            </div>

            <div
              v-if="report?.oldBatteryIdentifier"
              class="flex justify-between items-start py-2"
            >
              <dt>Alte Batterie</dt>
              <dd class="font-medium text-right" data-testid="tkd-old-battery">
                {{ report.oldBatteryIdentifier?.serialNumber }}
                ({{ report.oldBatteryIdentifier?.customizedNumber }})
              </dd>
            </div>
          </template>

          <div
            v-if="report?.newDevice"
            class="flex justify-between items-start py-2"
          >
            <dt>
              {{ report?.oldDevice ? 'Neues Gerät' : 'Gerät' }}
            </dt>
            <dd class="font-medium text-right" data-testid="tkd-new-device">
              {{ report.newDevice?.serialNumber }}
              ({{ report.newDevice?.customizedNumber }})
            </dd>
          </div>

          <template v-if="report?.newBatteryIdentifier">
            <div class="flex justify-between items-start py-2">
              <dt>{{ report?.oldDevice ? 'Neue Batterie' : 'Batterie' }}</dt>
              <dd class="font-medium text-right" data-testid="tkd-new-battery">
                {{ report.newBatteryIdentifier?.serialNumber }}
                ({{ report.newBatteryIdentifier?.customizedNumber }})
              </dd>
            </div>
          </template>
        </dl>
      </section>

      <section v-if="report?.accessoryListReport?.length">
        <h4 class="mt-0">Adapter</h4>
        <ul class="divide-y divide-light-3 dark:divide-dark-3 not-prose">
          <li
            v-for="(accessory, index) in report.accessoryListReport"
            :key="index"
            class="flex justify-between items-start py-2"
            :data-testid="`tkd-accessory-${index}`"
          >
            <div>
              <div
                v-if="accessory.type"
                class="font-medium"
                :data-testid="`tkd-accessory-type-${index}`"
              >
                {{ accessory.type }}
              </div>
              <div v-else :data-testid="`tkd-accessory-no-service-${index}`">
                Kein Service
              </div>
              <div :data-testid="`tkd-accessory-reason-${index}`">
                - {{ accessory.reason }}
              </div>
              <div :data-testid="`tkd-accessory-note-${index}`">
                {{ accessory.note }}
              </div>
            </div>
            <div class="font-medium text-right">
              <div :data-testid="`tkd-accessory-customized-number-${index}`">
                {{ accessory.customizedNumber }}
              </div>
              <div
                v-if="accessory.newCustomizedNumber"
                :data-testid="`tkd-accessory-new-customized-number-${index}`"
              >
                <em class="mdi mdi-arrow-right"></em>
                {{ accessory.newCustomizedNumber }}
              </div>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <h3>Fotos</h3>
    <ImageGallery
      v-if="images?.length"
      v-model:images="images"
      :hide-delete-button="true"
    />
    <p v-else>Keine Fotos vorhanden</p>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  DeviceClasses,
  SmartRadiatorThermostat,
} from '@/models/devices/Device';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { TKDService } from '@/models/installationPoint/TKDService';
import { Image } from '@/models/Image';
import {
  BatteryExchangeReasonEnum,
  DeviceServiceTypeEnum,
  noTKDPossibleReasons,
  noTKDPossibleServiceList,
  deviceServiceReasonList,
  deviceServiceList,
  resolveAccessoryService,
} from '@/enums/device';
import ImageGallery from '@/components/UI/Image/ImageGallery.vue';
import { DeviceEvent, EventType } from '@/models/installationPoint/DeviceEvent';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { formatDateTime } from '@/utils/dateUtils';

const tkdServiceModel = defineModel<TKDService>('tkdService');
const installationPointModel =
  defineModel<InstallationPoint>('installation-point');
const images = defineModel<Image[]>('images');
const currentDevice = defineModel<DeviceClasses>('currentDevice');
const entitiesStore = useEntitiesStore();

const report = computed(() => {
  const activeDevice = currentDevice.value;
  const radiatorDevice =
    activeDevice instanceof SmartRadiatorThermostat ? activeDevice : undefined;
  const installationPoint = installationPointModel.value;
  const tkdService = tkdServiceModel.value;
  const deviceService = tkdService?.deviceService;
  const deviceServiceType = deviceService?.type as
    | DeviceServiceTypeEnum
    | undefined;

  const isExchangeService =
    deviceServiceType === DeviceServiceTypeEnum.EXCHANGE;
  const isUnmountService = deviceServiceType === DeviceServiceTypeEnum.UNMOUNT;
  const isNoService =
    deviceServiceType === DeviceServiceTypeEnum.NO_SERVICE_POSSIBLE;

  const shouldShowCurrentDevice =
    deviceServiceType === DeviceServiceTypeEnum.EXCHANGE ||
    deviceServiceType === DeviceServiceTypeEnum.REPAIR ||
    deviceServiceType === DeviceServiceTypeEnum.REMAIN;

  const shouldShowOldHardware = isExchangeService || isUnmountService;

  const resolveDeviceById = (deviceId?: string) => {
    if (!deviceId) return undefined;
    const entity = entitiesStore.getEntityById(deviceId);
    if (entity instanceof SmartRadiatorThermostat) {
      return entity;
    }
    return undefined;
  };

  const resolvedOldDevice = shouldShowOldHardware
    ? resolveDeviceById(deviceService?.deviceId)
    : undefined;

  const resolvedNewDevice = shouldShowCurrentDevice
    ? (resolveDeviceById(deviceService?.newDeviceId) ?? radiatorDevice)
    : undefined;

  const serviceDate = tkdService?.timestamp
    ? formatDateTime(tkdService?.timestamp)
    : undefined;

  const oldDeviceMountEvent = resolvedOldDevice
    ? (installationPoint?.deviceEvents?.find(
        (ev) =>
          ev.deviceId === resolvedOldDevice.id &&
          ev.type === EventType.MOUNT_EVENT
      ) as DeviceEvent)
    : undefined;

  const oldDeviceMountDate = oldDeviceMountEvent
    ? formatDateTime(oldDeviceMountEvent.timestamp)
    : undefined;

  const oldDeviceIdentifier = resolvedOldDevice
    ? {
        serialNumber: resolvedOldDevice.serialNumber,
        customizedNumber: resolvedOldDevice.customizedNumber,
        mountDate: oldDeviceMountDate,
      }
    : undefined;

  const newDeviceIdentifier = resolvedNewDevice
    ? {
        serialNumber: resolvedNewDevice.serialNumber,
        customizedNumber: resolvedNewDevice.customizedNumber,
        serviceDate,
      }
    : undefined;

  const deviceServiceReason = deviceService?.reason;

  const reportTypeList = isNoService
    ? noTKDPossibleServiceList
    : deviceServiceList;

  const reasonList = isNoService
    ? noTKDPossibleReasons
    : deviceServiceReasonList;

  const reportType = reportTypeList.find(
    (opt) => opt.value === deviceServiceType
  );

  const reason = reasonList.find((opt) => opt.value === deviceServiceReason);

  const note = deviceService?.note || '';

  const counterPressure = resolvedNewDevice?.counterPressure;

  const batteryExchange = tkdService?.accessoryServices.find(
    (accessory) =>
      accessory.reason === BatteryExchangeReasonEnum.BATTERY_EXCHANGE
  );

  let oldBatteryIdentifier = undefined;
  let newBatteryIdentifier = undefined;
  if (batteryExchange) {
    const oldBattery = installationPoint?.accessories?.find(
      (accessory) => accessory.id === batteryExchange.accessoryId
    );

    oldBatteryIdentifier = {
      customizedNumber: oldBattery?.customizedNumber,
      serialNumber: oldBattery?.serialNumber,
    };

    const newBattery = installationPoint?.accessories?.find(
      (accessory) => accessory.id === batteryExchange.newAccessoryId
    );
    newBatteryIdentifier = {
      customizedNumber: newBattery?.customizedNumber,
      serialNumber: newBattery?.serialNumber,
    };
  }

  const accessoryListReport = (tkdService?.accessoryServices ?? [])
    .filter((accessory) => accessory?.id !== batteryExchange?.id)
    .map((accessory) => {
      const { typeName, reasonName } = resolveAccessoryService(
        accessory.type,
        accessory.reason
      );

      return {
        customizedNumber: installationPoint?.accessories.find(
          (item) => item.id === accessory.accessoryId
        )?.customizedNumber,
        newCustomizedNumber: installationPoint?.accessories.find(
          (item) => item.id === accessory.newAccessoryId
        )?.customizedNumber,
        type: typeName,
        reason: reasonName,
        note: accessory.note || '',
      };
    });

  return {
    reportType: reportType ?? {},
    reason: reason ?? undefined,
    note: note ?? '',
    accessoryListReport: accessoryListReport ?? [],
    counterPressure: counterPressure ?? undefined,
    oldBatteryIdentifier:
      shouldShowOldHardware && oldBatteryIdentifier
        ? oldBatteryIdentifier
        : undefined,
    newBatteryIdentifier: newBatteryIdentifier ?? undefined,
    serviceDate,
    newDevice: shouldShowCurrentDevice ? newDeviceIdentifier : undefined,
    oldDevice: shouldShowOldHardware ? oldDeviceIdentifier : undefined,
  };
});
</script>
